import React, { useEffect, MouseEvent, useRef, useCallback } from 'react';
import { Icon } from '@gumtree/ui-library';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import * as util from '@gumtree/ui-library/src/send-message/util';
import Message from '@gumtree/ui-library/src/send-message/message';
import {
    Title,
    TipDescription,
    SendMessageDescription,
    MessageEncourageDialog, 
    MessageEncourageDialogContent,
    ContactSeller,
    SellerName,
    PhoneNumberWrapper,
    PhoneNumber,
} from './message-encourage.style';
import revealPhoneUtil from '../reveal-phone-util';
import { VipState, VipUseDispatch } from '../../../reducers';
import { updateShowMessageEncourage, updateShowOutOfHoursMessage } from '../../../reducers/post-call';

const DIALOG_DATA_Q = 'vip-message-encourage-dialog';
const TIMER_DURATION = 3 * 60 * 1000; // 3 minutes

const MessageEncourage: React.FC<MessageEncourageProps> = ({ isOpen = false }) => {
    const dispatch = useDispatch() as VipUseDispatch;
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const {
        isMobile,
        advertId,
        sendReplyUrl,
        sellerContactDetails,
        baseConfig,
        adDetails,
        userLoggedIn,
        phoneNumber,
        postCall,
        replyConfirmationTracking,
    } = useSelector(selectState, shallowEqual);
    const outOfHours = postCall.showOutOfHoursMessage;
    const placeholder = util.getPlaceholderText({ isMobile: true, seoName: adDetails.l1CategorySeoName });

    const { clearStore } = util.useClearStoreMessage();
    const { sendMessage, sendState, errors } = util.useSendMessage({
        sendReplyUrl,
        advertId,
        token: adDetails.token,
        userLoggedIn,
        outOfHours,
        proAccount: adDetails.pro,
        sendMessageSuccessFunc: clearStore,
    });

    const onClose = useCallback(() => {
        dispatch(updateShowMessageEncourage(false));
        dispatch(updateShowOutOfHoursMessage(false));
    }, [dispatch]);

    const clearTimer = useCallback(() => {
        timerRef.current && clearTimeout(timerRef.current);
    }, []);

    /**
     * only lasts 3 minutes since the user clicks “call” CTA, 
     * if the user doesn't fill or click within 3 minutes, then the pop-up message auto-close
     */
    useEffect(() => {
        if (!isOpen || outOfHours) return;

        timerRef.current = setTimeout(onClose, TIMER_DURATION);

        const dialog = document.querySelector(`[data-q="${DIALOG_DATA_Q}"]`);
        dialog?.addEventListener('click', clearTimer);

        return () => {
            clearTimer();
            dialog?.removeEventListener('click', clearTimer);
        };
    }, [isOpen, onClose, clearTimer]);
    
    const onCallClick = (e: MouseEvent<HTMLButtonElement>) => {
        revealPhoneUtil(e, dispatch, sellerContactDetails, adDetails, baseConfig, 'tel');
    };

    const onSendMessageClick = (e: MouseEvent<HTMLButtonElement>, params: util.SendMessageParams) => {
        sendMessage(e, params, {
            listingDetails: replyConfirmationTracking,
            listingPartner: adDetails.isFromMotorsFeed ? 'motors' : undefined,
            isPhoneNumberFilled: params.phoneNumber ? 'yes' : 'no',
            isMessageTextFilledValid: (params.message && params.message !== placeholder) ? 'yes' : 'no',
            isOutOfHourPopup: outOfHours ? 'yes' : 'no',
        });
    };

    return (
        <MessageEncourageDialog
            isOpen={isOpen}
            onClose={onClose}
            data-q={DIALOG_DATA_Q}
        >
            <MessageEncourageDialogContent>
                {
                    outOfHours ? (
                        <>
                            <Title>Dealer may be out of hours</Title>
                            <TipDescription>
                                Your call may not be answered out of hours, email your number and they’ll get back to you.
                            </TipDescription>
                        </>
                    ) : (
                        <>
                            <Title>Send a message</Title>
                            <SendMessageDescription>
                                Not able to get through? Send a message and the dealer will get back to you as soon as possible.
                            </SendMessageDescription>
                        </>
                    )
                }

                <Message
                    replyPhone={isMobile && outOfHours}
                    providePhone
                    advertId={advertId}
                    sendState={sendState}
                    errors={errors}
                    textareaAutoResize
                    messageUseStore={isMobile}
                    isCurrentUserSeller={adDetails.isCurrentUserSeller}
                    onSendMessageClick={onSendMessageClick}
                    onCallClick={onCallClick}
                    defaultMessage={placeholder}
                    defaultPhoneNumber={phoneNumber}
                />

                {!isMobile && outOfHours && (
                    <ContactSeller>
                        <SellerName>Contact {sellerContactDetails.contactName}</SellerName>
                        <PhoneNumberWrapper>
                            <Icon type="phone" color='foregroundSubdued' />
                            <PhoneNumber>{sellerContactDetails.revealedNumber.data || sellerContactDetails.replyPhone}</PhoneNumber>
                        </PhoneNumberWrapper>
                    </ContactSeller>
                )}
            </MessageEncourageDialogContent>
        </MessageEncourageDialog>
    );
};

export const selectState = ({
    adDetails,
    sellerContactDetails,
    sellerActions,
    baseConfig,
    userData,
    postCall,
}: VipState) => ({
    adDetails,
    baseConfig,
    postCall,
    sellerContactDetails,
    isMobile: baseConfig.device?.type === 'mobile',
    advertId: `${adDetails.id}`,
    userLoggedIn: userData?.userLoggedIn,
    sendReplyUrl: sellerActions.sendReplyUrl,
    phoneNumber: sellerContactDetails.phoneNumber,
    replyConfirmationTracking: {
        id: adDetails.id,
        isTrade: adDetails.sellerType === 'Trade',
        subcategory1: adDetails.l1Category,
        subcategory2: adDetails.l2CategoryId,
    },
});
interface MessageEncourageProps {
    isOpen: boolean;
}

export default MessageEncourage;
